import axios from "axios";
import useAuthStore from "../store/authStore";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "http://localhost:8000/api/v1",
  withCredentials: true,
});

// Attach the correct access token to every request
api.interceptors.request.use((config) => {
  if (config.headers.Authorization) return config;
  const { seekerToken, employerToken } = useAuthStore.getState();
  const isEmployerRoute = config.url?.startsWith("/employer");

  if (isEmployerRoute && employerToken) {
    config.headers.Authorization = `Bearer ${employerToken}`;
  } else if (!isEmployerRoute && seekerToken) {
    config.headers.Authorization = `Bearer ${seekerToken}`;
  }
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }

    const isEmployerRoute = original.url?.startsWith("/employer");

    const skipEmployer =
      original.url?.includes("/employer/auth/refresh") ||
      original.url?.includes("/employer/auth/login") ||
      original.url?.includes("/employer/auth/register");

    const skipSeeker =
      original.url?.includes("/auth/refresh") ||
      original.url?.includes("/auth/login") ||
      original.url?.includes("/auth/register");

    // Employer token refresh
    if (isEmployerRoute && !skipEmployer) {
      original._retry = true;
      const { employerToken } = useAuthStore.getState();
      try {
        const { data } = await api.post("/employer/auth/refresh");
        useAuthStore
          .getState()
          .setEmployerAuth(data.access_token, data.employer);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch {
        useAuthStore.getState().clearEmployerAuth();
        // Only redirect if there was a session to lose. Otherwise this is
        // a probe (e.g. App.jsx restore) and should fail silently.
        if (employerToken) {
          window.location.href = "/employers/login";
        }
        return Promise.reject(err);
      }
    }

    // Seeker token refresh
    if (!isEmployerRoute && !skipSeeker) {
      original._retry = true;
      const { seekerToken } = useAuthStore.getState();
      try {
        const { data } = await api.post("/auth/refresh");
        useAuthStore.getState().setSeekerAuth(data.access_token, data.user);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch {
        useAuthStore.getState().clearSeekerAuth();
        if (seekerToken) {
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  },
);

export default api;
